import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "_shared/_stores/redux-store";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import type { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import type { ProposalChangeDescriptionContext } from "work/proposals/negotiation/proposal-change-description";
import type { ProposalChangeLine } from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalFeeScheduleTeamMemberUserIds } from "work/proposals/negotiation/proposal-fee-schedule-aspects";

/**
 * Supplies the two things the change formatter cannot derive on its own: a person's name, and the label
 * of the collection item a change landed on.
 *
 * People are fetched here rather than read only from whatever happens to be cached. Reading the cache
 * alone is what left "Added Team Member" standing in a save confirmation with nobody named. The query is
 * cache-aware and asks only for ids the store is missing. Every session that renders a proposal may make
 * that ask: the by-id lookups stand behind `MarketplaceDirectoryAccess`, which the restricted onboarding
 * tranche grants (issue #4830), so there is no viewer this hook has to be switched off for and no
 * caller-supplied name list standing in for the Individuals store.
 *
 * The projection passed in is the EFFECTIVE one — projection plus pending local operations — so a member
 * added but not yet saved is named in the dialog that asks whether to save them.
 *
 * `baselineProjection` is the server's own, and it is what makes a REMOVAL describable. An effective
 * projection cannot name what a pending removal has taken out of it — that removal is precisely why the
 * item is gone — and the operation cannot name it either, because the collection diff canonicalizes its
 * `expectedValue` down to an identity so display metadata never crosses the wire. So removing one of two
 * policy documents asked the user to confirm "Removed Client Policy Document", naming neither. The
 * baseline still holds everything the pending work has not been saved over; the effective projection is
 * indexed second and wins, so a rename still reads as the new name.
 *
 * `changeLines` are the SAVED, still-unpublished changes, and they name the one kind of person no
 * projection can: the person a change moved away FROM. A reassignment or a roster removal that has been
 * saved is already absent from the baseline — the baseline is the server's projection, and the save is
 * in it — so once a reload has dropped the page's incidental caches, the line's own `previousValue` is
 * the only place the former assignee or the departed member survives. Pending operations need no such
 * treatment: an operation's `expectedValue` is by definition what the baseline still holds.
 */
export function useProposalChangeDescriptionContext(
  projection: ProposalJSON | undefined,
  baselineProjection?: ProposalJSON,
  changeLines?: readonly ProposalChangeLine[],
): ProposalChangeDescriptionContext {
  const individualEntries = useSelector((state: RootState) => state.individuals?.byUserId?.entries);
  const referencedUserIds = useMemo(
    () =>
      new Set([
        ...collectReferencedUserIds(baselineProjection),
        ...collectReferencedUserIds(projection),
        ...collectSupersededUserIds(changeLines),
      ]),
    [baselineProjection, changeLines, projection],
  );
  const missingUserIds = useMemo(() => {
    const missing = new Set<string>();
    for (const userId of referencedUserIds) {
      if (!individualEntries?.[userId]) missing.add(userId);
    }
    return missing;
  }, [individualEntries, referencedUserIds]);
  // Keyed on the id set itself, so this settles: a fulfilled fetch lands the people in the store, which
  // empties `missingUserIds`, which parks the query on skipToken with the names still resolvable below.
  const individualQuery = useGetIndividualsByUserIdsQuery(
    missingUserIds.size ? { userIds: missingUserIds } : skipToken,
  );
  const fetchedIndividuals = individualQuery.data;

  return useMemo(() => {
    const fetchedNames = new Map<string, string>();
    for (const individual of fetchedIndividuals ?? []) {
      const name = individualName(individual);
      if (individual.userId && name) fetchedNames.set(individual.userId, name);
    }

    // The store first (it holds everyone already known to the page), then this hook's own fetch, which
    // covers the render between the request landing and the store update.
    const resolveUserName = (userId: string) =>
      individualName(individualEntries?.[userId]) ?? fetchedNames.get(userId);
    // Baseline first, effective second: the later write wins, so an item both hold is named by the
    // value the user is looking at, while one only the baseline still holds stays nameable.
    const itemLabels = buildItemLabelIndex(baselineProjection, resolveUserName);
    for (const [key, label] of buildItemLabelIndex(projection, resolveUserName)) itemLabels.set(key, label);

    // Same two projections, same precedence, at whole-item granularity: a change addresses one column
    // of a fee-schedule category, and the summary draws the whole category around it (#4845).
    const itemValues = buildItemValueIndex(baselineProjection);
    for (const [key, value] of buildItemValueIndex(projection)) itemValues.set(key, value);

    return {
      resolveUserName,
      // Ids are matched case-insensitively, the way every other identity comparison in the negotiation
      // does it: an id's casing depends on which layer serialized it, and a label that fails to match is
      // indistinguishable from an item with no name.
      resolveItemLabel: (path: string, itemId: string) =>
        itemLabels.get(`${path}|${itemId.toLowerCase()}`) ?? itemLabels.get(itemId.toLowerCase()),
      resolveItemValue: (path: string, itemId: string) =>
        itemValues.get(`${path}|${itemId.toLowerCase()}`) ?? itemValues.get(itemId.toLowerCase()),
    };
  }, [baselineProjection, fetchedIndividuals, individualEntries, projection]);
}

function individualName(individual: IndividualJSON | undefined): string | undefined {
  if (!individual) return undefined;
  const name = [individual.firstName, individual.lastName].filter(Boolean).join(" ").trim();
  return name || undefined;
}

/**
 * Every person a change on this proposal can be about: both teams' leaders and members, both sides'
 * reviewers, and the creator. Team changes are the reason this exists — they carry ids and nothing else
 * (`EntityMemberJSON` is two GUIDs), so without a name for the id a row reads "Added Team Member" and
 * leaves the reader to guess which one.
 */
function collectReferencedUserIds(projection: ProposalJSON | undefined): Set<string> {
  const userIds = new Set<string>();
  if (!projection) return userIds;

  const add = (userId: string | null | undefined) => {
    if (typeof userId === "string" && userId) userIds.add(userId);
  };

  for (const team of [projection.clientTeam, projection.vendorTeam]) {
    add(team?.leader?.userId);
    for (const member of team?.members ?? []) add(member?.userId);
  }
  for (const reviewer of [...(projection.clientReviewers ?? []), ...(projection.vendorReviewers ?? [])]) {
    add(reviewer?.userId);
  }
  // A fee category's assigned member is usually on the vendor team already, but not necessarily still:
  // the assignment outlives the roster, and the change that reassigns it names both people (#4949).
  for (const category of projection.feeSchedule?.categories ?? []) add(category?.teamMember?.userId);
  add(projection.creator?.userId);

  return userIds;
}

/**
 * Every person named ONLY by what a saved change replaced — the assignee a fee category was moved away
 * from, the member a roster removal took off the team. Both are gone from the projections by the time
 * the summary renders them, so a lookup built from the projections alone left the summary reading
 * "Team member → Dana Fox": the newcomer named, their predecessor reduced to the fallback word.
 *
 * Read from exactly the two shapes the description formatter itself resolves a person out of — the fee
 * schedule's `teamMember` column, and a top-level `userId`/`reviewerUserId` identity — so this asks for
 * precisely the ids that would otherwise go unnamed, and never crawls a payload for ids nothing renders.
 */
function collectSupersededUserIds(changeLines: readonly ProposalChangeLine[] | undefined): Set<string> {
  const userIds = new Set<string>();
  for (const line of changeLines ?? []) {
    if (!line.previousValueExists) continue;
    const previousValue = line.previousValue;

    for (const userId of proposalFeeScheduleTeamMemberUserIds({
      path: line.path,
      conflictKey: line.conflictKey,
      expectedValueExists: true,
      expectedValue: previousValue,
    })) {
      userIds.add(userId);
    }

    if (previousValue && typeof previousValue === "object" && !Array.isArray(previousValue)) {
      for (const key of ["userId", "reviewerUserId"] as const) {
        const userId = (previousValue as Record<string, unknown>)[key];
        if (typeof userId === "string" && userId) userIds.add(userId);
      }
    }
  }
  return userIds;
}

/**
 * Names a person from whatever the Individuals store already holds. For a surface that has already
 * asked for the people it shows — the change history fetches the users its entries name, the field
 * history menu the authors and members its candidates name — and only needs to read the answer back.
 * The resolver is stable across renders until the store's entries change.
 */
export function useProposalUserNameResolver(): (userId: string) => string | undefined {
  const individualEntries = useSelector((state: RootState) => state.individuals?.byUserId?.entries);
  return useMemo(() => (userId: string) => individualName(individualEntries?.[userId]), [individualEntries]);
}

/**
 * Indexes the projection's collection items by id so a change on "/feeSchedule/categories" can say which
 * category it touched. Keyed both by path+id and by bare id: an operation and a timeline entry name the
 * same item through slightly different paths (one carries the collection path, the other the item's).
 */
/**
 * Indexes the projection's collection items by id, whole — the sibling of {@link buildItemLabelIndex},
 * keyed identically, for a surface that needs more of an item than its name.
 *
 * Only fee-schedule categories are indexed, because they are the only collection whose whole value a
 * change surface reads today: a fee-schedule change is drawn as the entire category with the columns
 * that moved blue-lined inside it, and the columns that did not move live here rather than in the
 * negotiation stream. A document or a team member is fully described by its name, which the label index
 * already gives, so indexing them would be weight with no reader.
 */
export function buildItemValueIndex(projection: ProposalJSON | undefined): Map<string, unknown> {
  const values = new Map<string, unknown>();
  for (const category of projection?.feeSchedule?.categories ?? []) {
    const id = category?.id;
    if (typeof id !== "string" || !id) continue;
    values.set(`/feeSchedule/categories|${id.toLowerCase()}`, category);
    values.set(id.toLowerCase(), category);
  }
  return values;
}

export function buildItemLabelIndex(
  projection: ProposalJSON | undefined,
  resolveUserName: (userId: string) => string | undefined = () => undefined,
): Map<string, string> {
  const labels = new Map<string, string>();
  if (!projection) return labels;

  const record = (path: string, id: unknown, label: unknown) => {
    if (typeof id !== "string" || !id) return;
    if (typeof label !== "string" || !label) return;
    labels.set(`${path}|${id.toLowerCase()}`, label);
    labels.set(id.toLowerCase(), label);
  };

  // A team member's name IS its label. The member itself is two ids (`EntityMemberJSON`), so without this
  // a roster change had nothing to name it in the list's subject line and read as an anonymous "Team
  // Member" row. Its identity is the composite the operation layer mints — a person can sit on a proposal
  // for more than one legal entity — and the bare user id, since a change line may carry either.
  for (const [path, team] of [
    ["/clientTeam/members", projection.clientTeam],
    ["/vendorTeam/members", projection.vendorTeam],
  ] as const) {
    for (const member of team?.members ?? []) {
      const name = member?.userId ? resolveUserName(member.userId) : undefined;
      if (!name) continue;
      record(path, member.userId, name);
      if (member.legalEntityId) record(path, `${member.userId}:${member.legalEntityId}`, name);
    }
  }

  for (const category of projection.feeSchedule?.categories ?? []) {
    record("/feeSchedule/categories", category?.id, category?.name);
  }
  for (const [path, documents] of [
    ["/conflicts/documents", projection.conflicts?.documents],
    ["/clientPolicies/documents", projection.clientPolicies?.documents],
    ["/vendorPolicies/documents", projection.vendorPolicies?.documents],
  ] as const) {
    for (const document of documents ?? []) {
      // Both ids: a document reference and the file it wraps carry ids of their own, and which one a
      // change line names depends on the layer that recorded it — indexing only the first left the other
      // resolving to nothing, i.e. a document row with no file name on it.
      record(path, document?.documentId, document?.documentInfo?.name);
      record(path, document?.documentInfo?.id, document?.documentInfo?.name);
    }
  }

  return labels;
}
